import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  IconButton,
  Tooltip,
  Collapse,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import {
  Refresh as RefreshIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Speed as SpeedIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Storage as StorageIcon,
  Timeline as TimelineIcon,
} from '@material-ui/icons';
import { format } from 'date-fns';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  statsCard: {
    height: '100%',
    position: 'relative',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  statsIcon: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    color: theme.palette.text.secondary,
    opacity: 0.3,
    fontSize: 48,
  },
  recommendationCard: {
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  },
  actionButton: {
    margin: theme.spacing(1),
  },
  progressContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  progressText: {
    marginTop: theme.spacing(1),
    textAlign: 'center',
  },
  dangerButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  successChip: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
  },
  warningChip: {
    backgroundColor: theme.palette.warning.main,
    color: 'white',
  },
  infoSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
}));

const GPSArchiveManager = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [migrateDialog, setMigrateDialog] = useState(false);
  const [migrationAnalysis, setMigrationAnalysis] = useState(null);
  const [archiveOptions, setArchiveOptions] = useState({
    daysToKeep: 90,
    dryRun: true,
  });
  const [deleteOptions, setDeleteOptions] = useState({
    daysToKeep: 365,
    dryRun: true,
    confirmDelete: false,
  });
  const [migrateOptions, setMigrateOptions] = useState({
    dryRun: true,
  });
  const [operationProgress, setOperationProgress] = useState(null);
  const [operationResult, setOperationResult] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    typeDistribution: false,
    topCrews: false,
  });

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/company/gps-archive/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecommendations = useCallback(async () => {
    try {
      const response = await axios.get('/api/company/gps-archive/recommendations');
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchRecommendations();
  }, [fetchStats, fetchRecommendations]);

  const handleArchive = async () => {
    setOperationProgress({ type: 'archive', progress: 0 });
    setOperationResult(null);

    try {
      const params = new URLSearchParams({
        daysToKeep: archiveOptions.daysToKeep.toString(),
        dryRun: archiveOptions.dryRun.toString(),
      });
      
      const eventSource = new EventSource(
        `/api/company/gps-archive/archive?${params}`
      );

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'progress') {
          setOperationProgress({
            type: 'archive',
            ...data,
          });
        } else if (data.type === 'complete') {
          setOperationResult(data.result);
          setOperationProgress(null);
          eventSource.close();
          fetchStats();
          fetchRecommendations();
          if (!archiveOptions.dryRun) {
            setArchiveDialog(false);
          }
        } else if (data.type === 'error') {
          setOperationResult({ success: false, error: data.error });
          setOperationProgress(null);
          eventSource.close();
        }
      };

      eventSource.onerror = () => {
        setOperationProgress(null);
        eventSource.close();
      };
    } catch (error) {
      console.error('Error archiving:', error);
      setOperationResult({ success: false, error: error.message });
      setOperationProgress(null);
    }
  };

  const handleDelete = async () => {
    setOperationProgress({ type: 'delete', progress: 0 });
    setOperationResult(null);

    try {
      const response = await axios.post('/api/company/gps-archive/delete', deleteOptions);
      setOperationResult(response.data);
      setOperationProgress(null);
      
      if (!deleteOptions.dryRun && response.data.success) {
        setDeleteDialog(false);
        fetchStats();
        fetchRecommendations();
      }
    } catch (error) {
      console.error('Error deleting:', error);
      setOperationResult({ 
        success: false, 
        error: error.response?.data?.error || error.message 
      });
      setOperationProgress(null);
    }
  };

  const handleOptimize = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/company/gps-archive/optimize');
      setOperationResult(response.data);
      fetchStats();
    } catch (error) {
      console.error('Error optimizing:', error);
      setOperationResult({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const analyzeMigration = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/company/gps-archive/migrate/analyze');
      setMigrationAnalysis(response.data.data);
      setMigrateDialog(true);
    } catch (error) {
      console.error('Error analyzing migration:', error);
      setOperationResult({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleMigrate = async () => {
    setOperationProgress({ type: 'migrate', progress: 0 });
    setOperationResult(null);

    try {
      const response = await fetch('/api/company/gps-archive/migrate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(migrateOptions),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'progress') {
                setOperationProgress({
                  type: 'migrate',
                  ...data,
                });
              } else if (data.type === 'complete') {
                setOperationResult(data.result);
                setOperationProgress(null);
                if (!migrateOptions.dryRun) {
                  setMigrateDialog(false);
                }
                fetchStats();
              } else if (data.type === 'error') {
                setOperationResult({ success: false, error: data.error });
                setOperationProgress(null);
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error migrating:', error);
      setOperationResult({ success: false, error: error.message });
      setOperationProgress(null);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatBytes = (bytes) => {
    const mb = bytes / 1024 / 1024;
    return mb.toFixed(2) + ' MB';
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4" gutterBottom>
          GPS Activity Archive Manager
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage and optimize your GPS activity data storage
        </Typography>
      </div>

      {loading && <LinearProgress />}

      {operationResult && (
        <Alert 
          severity={operationResult.success ? 'success' : 'error'} 
          onClose={() => setOperationResult(null)}
          style={{ marginBottom: 16 }}
        >
          <AlertTitle>{operationResult.success ? 'Success' : 'Error'}</AlertTitle>
          {operationResult.message || operationResult.error}
          
          {/* Special handling for dry run results */}
          {operationResult.dryRun && operationResult.stats?.sampleRecords && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Sample records to be archived:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                {operationResult.stats.sampleRecords.slice(0, 5).map((record, index) => (
                  <li key={index}>
                    <Typography variant="body2">
                      Created: {new Date(record.created).toLocaleString()}, 
                      Type: {record.type || 'N/A'}, 
                      User: {record.user || 'N/A'}
                    </Typography>
                  </li>
                ))}
                {operationResult.stats.sampleRecords.length > 5 && (
                  <Typography variant="body2" color="textSecondary">
                    ...and {operationResult.stats.sampleRecords.length - 5} more
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          
          {operationResult.stats && (
            <Box mt={1}>
              {Object.entries(operationResult.stats)
                .filter(([key]) => key !== 'sampleRecords') // Don't show sampleRecords here
                .map(([key, value]) => {
                // Handle different types of values
                if (Array.isArray(value)) {
                  // For arrays (like sampleRecords), show count
                  return (
                    <Typography key={key} variant="body2">
                      {key}: {value.length} items
                    </Typography>
                  );
                } else if (typeof value === 'object' && value !== null) {
                  // For objects, stringify or show [Object]
                  return (
                    <Typography key={key} variant="body2">
                      {key}: {JSON.stringify(value)}
                    </Typography>
                  );
                } else {
                  // For simple values, display directly
                  return (
                    <Typography key={key} variant="body2">
                      {key}: {value}
                    </Typography>
                  );
                }
              })}
            </Box>
          )}
        </Alert>
      )}

      {/* Statistics Grid */}
      {stats && (
        <Grid container spacing={3} style={{ marginBottom: 24 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <StorageIcon className={classes.statsIcon} />
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Documents
                </Typography>
                <Typography variant="h5">
                  {formatNumber(stats.totalDocuments)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ~{stats.estimatedSizeMB} MB
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <TimelineIcon className={classes.statsIcon} />
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Date Range
                </Typography>
                <Typography variant="body2">
                  {stats.dateRange.oldest 
                    ? format(new Date(stats.dateRange.oldest), 'MMM dd, yyyy')
                    : 'N/A'}
                </Typography>
                <Typography variant="body2">
                  to {stats.dateRange.newest 
                    ? format(new Date(stats.dateRange.newest), 'MMM dd, yyyy')
                    : 'N/A'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <ArchiveIcon className={classes.statsIcon} />
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Older than 90 days
                </Typography>
                <Typography variant="h5">
                  {formatNumber(stats.distribution.olderThan90Days)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {((stats.distribution.olderThan90Days / stats.totalDocuments) * 100).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card className={classes.statsCard}>
              <DeleteIcon className={classes.statsIcon} />
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Older than 1 year
                </Typography>
                <Typography variant="h5">
                  {formatNumber(stats.distribution.olderThanYear)}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {((stats.distribution.olderThanYear / stats.totalDocuments) * 100).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Paper style={{ padding: 16, marginBottom: 24 }}>
          <Typography variant="h6" gutterBottom>
            <InfoIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Recommendations
          </Typography>
          {recommendations.map((rec, index) => (
            <Card key={index} className={classes.recommendationCard}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  <WarningIcon color="inherit" style={{ verticalAlign: 'middle', marginRight: 8 }} />
                  {rec.suggestion}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {rec.reason}
                </Typography>
                {rec.estimatedReduction && (
                  <Chip 
                    label={rec.estimatedReduction} 
                    size="small" 
                    style={{ marginTop: 8 }}
                    className={classes.warningChip}
                  />
                )}
              </CardContent>
            </Card>
          ))}
        </Paper>
      )}

      {/* Action Buttons */}
      <Paper style={{ padding: 24, marginBottom: 24 }}>
        <Typography variant="h6" gutterBottom>
          Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              startIcon={<RefreshIcon />}
              onClick={fetchStats}
              disabled={loading}
            >
              Refresh Stats
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              size="large"
              startIcon={<ArchiveIcon />}
              onClick={() => setArchiveDialog(true)}
              disabled={loading || operationProgress}
            >
              Archive Old Data
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              className={classes.dangerButton}
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialog(true)}
              disabled={loading || operationProgress}
            >
              Delete Old Data
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<SpeedIcon />}
              onClick={handleOptimize}
              disabled={loading || operationProgress}
            >
              Optimize Collection
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              startIcon={<TimelineIcon />}
              onClick={analyzeMigration}
              disabled={loading || operationProgress}
              style={{ backgroundColor: '#4caf50' }}
            >
              Migrate to Improved Schema (~60% Space Savings)
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Additional Statistics */}
      {stats && (
        <>
          {/* Type Distribution */}
          <Paper style={{ padding: 16, marginBottom: 16 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">
                Activity Type Distribution
              </Typography>
              <IconButton onClick={() => toggleSection('typeDistribution')}>
                {expandedSections.typeDistribution ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={expandedSections.typeDistribution}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Percentage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.typeDistribution.map((item) => (
                      <TableRow key={item._id || 'unknown'}>
                        <TableCell>{item._id || 'Unknown'}</TableCell>
                        <TableCell align="right">{formatNumber(item.count)}</TableCell>
                        <TableCell align="right">
                          {((item.count / stats.totalDocuments) * 100).toFixed(2)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>

          {/* Top Crews */}
          <Paper style={{ padding: 16 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">
                Top Crews by Activity
              </Typography>
              <IconButton onClick={() => toggleSection('topCrews')}>
                {expandedSections.topCrews ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={expandedSections.topCrews}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Crew Name</TableCell>
                      <TableCell align="right">Activity Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.topCrews.map((crew) => (
                      <TableRow key={crew.crewId}>
                        <TableCell>{crew.crewName}</TableCell>
                        <TableCell align="right">{formatNumber(crew.count)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Paper>
        </>
      )}

      {/* Archive Dialog */}
      <Dialog 
        open={archiveDialog} 
        onClose={() => !operationProgress && setArchiveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Archive GPS Activities</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Alert severity="info">
              Archiving moves old data to a separate collection while keeping it accessible 
              for historical reports. This improves performance of the main collection.
            </Alert>
          </Box>
          
          <TextField
            label="Days to Keep in Main Collection"
            type="number"
            fullWidth
            value={archiveOptions.daysToKeep}
            onChange={(e) => setArchiveOptions({ 
              ...archiveOptions, 
              daysToKeep: parseInt(e.target.value) 
            })}
            margin="normal"
            disabled={operationProgress}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={archiveOptions.dryRun}
                onChange={(e) => setArchiveOptions({ 
                  ...archiveOptions, 
                  dryRun: e.target.checked 
                })}
                disabled={operationProgress}
              />
            }
            label="Dry Run (show what would be archived without doing it)"
          />

          {operationProgress && (
            <Box className={classes.progressContainer}>
              <LinearProgress 
                variant="determinate" 
                value={parseFloat(operationProgress.progress || 0)} 
              />
              <Typography variant="body2" className={classes.progressText}>
                {operationProgress.progress}% complete 
                ({formatNumber(operationProgress.processed || 0)} / {formatNumber(operationProgress.total || 0)})
              </Typography>
              {operationProgress.archived > 0 && (
                <Typography variant="body2" className={classes.progressText}>
                  Archived: {formatNumber(operationProgress.archived)}, 
                  Deleted: {formatNumber(operationProgress.deleted || 0)}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setArchiveDialog(false)} 
            disabled={operationProgress}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleArchive} 
            color="primary" 
            variant="contained"
            disabled={operationProgress}
            startIcon={operationProgress ? <CircularProgress size={16} /> : <ArchiveIcon />}
          >
            {operationProgress ? 'Archiving...' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog 
        open={deleteDialog} 
        onClose={() => !operationProgress && setDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Old GPS Activities</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Alert severity="error">
              <AlertTitle>Warning</AlertTitle>
              This will permanently delete GPS activity data. This action cannot be undone.
              Make sure you have backups before proceeding.
            </Alert>
          </Box>
          
          <TextField
            label="Days to Keep"
            type="number"
            fullWidth
            value={deleteOptions.daysToKeep}
            onChange={(e) => setDeleteOptions({ 
              ...deleteOptions, 
              daysToKeep: parseInt(e.target.value) 
            })}
            margin="normal"
            disabled={operationProgress}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={deleteOptions.dryRun}
                onChange={(e) => setDeleteOptions({ 
                  ...deleteOptions, 
                  dryRun: e.target.checked 
                })}
                disabled={operationProgress}
              />
            }
            label="Dry Run (show what would be deleted without doing it)"
          />

          {!deleteOptions.dryRun && (
            <FormControlLabel
              control={
                <Switch
                  checked={deleteOptions.confirmDelete}
                  onChange={(e) => setDeleteOptions({ 
                    ...deleteOptions, 
                    confirmDelete: e.target.checked 
                  })}
                  disabled={operationProgress}
                />
              }
              label="I understand this will permanently delete data"
            />
          )}

          {operationProgress && (
            <Box className={classes.progressContainer}>
              <LinearProgress 
                variant="determinate" 
                value={parseFloat(operationProgress.progress || 0)} 
              />
              <Typography variant="body2" className={classes.progressText}>
                {operationProgress.progress}% complete
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog(false)} 
            disabled={operationProgress}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            className={classes.dangerButton}
            variant="contained"
            disabled={operationProgress || (!deleteOptions.dryRun && !deleteOptions.confirmDelete)}
            startIcon={operationProgress ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {operationProgress ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Migrate Dialog */}
      <Dialog 
        open={migrateDialog} 
        onClose={() => !operationProgress && setMigrateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Migrate to Improved GPS Schema</DialogTitle>
        <DialogContent>
          {migrationAnalysis && (
            <Box mb={3}>
              <Alert severity="success">
                <AlertTitle>Migration Analysis Results</AlertTitle>
                <Typography variant="body2">
                  Sample size analyzed: {formatNumber(migrationAnalysis.sampleSize)} records
                </Typography>
                <Typography variant="body2">
                  Average record size: {migrationAnalysis.avgOldSize} bytes → {migrationAnalysis.avgNewSize} bytes
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  Space reduction: {migrationAnalysis.reductionPercent}%
                </Typography>
                <Divider style={{ margin: '8px 0' }} />
                <Typography variant="body2">
                  Total records: {formatNumber(migrationAnalysis.totalRecords)}
                </Typography>
                <Typography variant="body2">
                  Estimated current size: {migrationAnalysis.estimatedOldSizeMB} MB
                </Typography>
                <Typography variant="body2">
                  Estimated new size: {migrationAnalysis.estimatedNewSizeMB} MB
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                  Estimated savings: {migrationAnalysis.estimatedSavingsMB} MB
                </Typography>
              </Alert>
            </Box>
          )}
          
          <Alert severity="info" style={{ marginBottom: 16 }}>
            <AlertTitle>About Schema Migration</AlertTitle>
            <Typography variant="body2">
              This migration will:
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
              <li>Convert string fields (lat, long, speed, etc.) to numbers</li>
              <li>Add GeoJSON support for better location queries</li>
              <li>Improve indexes for faster queries</li>
              <li>Reduce storage by approximately 60%</li>
            </ul>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              The migration creates new documents in the improved collection. 
              Original data remains untouched until you verify and switch over.
            </Typography>
          </Alert>
          
          <FormControlLabel
            control={
              <Switch
                checked={migrateOptions.dryRun}
                onChange={(e) => setMigrateOptions({ 
                  ...migrateOptions, 
                  dryRun: e.target.checked 
                })}
                disabled={operationProgress}
              />
            }
            label="Dry Run (analyze without migrating)"
          />

          {operationProgress && operationProgress.type === 'migrate' && (
            <Box className={classes.progressContainer}>
              <LinearProgress 
                variant="determinate" 
                value={parseFloat(operationProgress.progress || 0)} 
              />
              <Typography variant="body2" className={classes.progressText}>
                {operationProgress.progress}% complete 
                ({formatNumber(operationProgress.processed || 0)} / {formatNumber(operationProgress.total || 0)})
              </Typography>
              {operationProgress.migrated > 0 && (
                <Typography variant="body2" className={classes.progressText}>
                  Migrated: {formatNumber(operationProgress.migrated)}, 
                  Errors: {formatNumber(operationProgress.errors || 0)}
                </Typography>
              )}
            </Box>
          )}

          {!migrateOptions.dryRun && (
            <Alert severity="warning" style={{ marginTop: 16 }}>
              <AlertTitle>Next Steps After Migration</AlertTitle>
              <Typography variant="body2">
                1. Update your mobile app code to use GPSActivityImproved model<br />
                2. Test the new collection thoroughly<br />
                3. Switch your application to use the new model<br />
                4. Archive or delete the old collection
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setMigrateDialog(false)} 
            disabled={operationProgress}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleMigrate} 
            color="primary" 
            variant="contained"
            disabled={operationProgress}
            startIcon={operationProgress ? <CircularProgress size={16} /> : <TimelineIcon />}
          >
            {operationProgress ? 'Migrating...' : 'Start Migration'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GPSArchiveManager;